import { useState } from "react";
import { useForm } from "react-hook-form";
import { Check, Lock as LockIcon } from "lucide-react";
import * as accountApi from "../../../api/account/api";
import { AuthStatus } from "../../../types/domain";
import { BackToLoginLink } from "../components/BackToLoginLink";
import { ErrorAlert } from "../components/ErrorAlert";
import { PasswordVisibilityToggle } from "../../../components/common/PasswordVisibilityToggle";
import { SubmitButton } from "../components/SubmitButton";
import { LoginFormField } from "../components/LoginFormField";
import { StatusIconCircle } from "@/components/common/StatusIconCircle";
import { ViewHeading } from "../components/ViewHeading";
import { useApiError } from "../hooks/useApiError";
import { usePasswordVisibility } from "../../../hooks/usePasswordVisibility";
import { LoginViewState } from "../types";
import { mustMatch, PASSWORD_MAX_INPUT_LENGTH, validateNewPassword, validatePasswordLength } from "../utils/validators";
import { FormOverlay } from "@/components/common/Loading";

type Props = {
  account: string;
  email: string;
  token: string;
  onViewChange: (view: LoginViewState) => void;
};

type ResetPasswordFormValues = {
  password: string;
  confirmPassword: string;
};

export const ResetPasswordView = ({ account, email, token, onViewChange }: Props) => {
  const { apiError, setApiError, clearError } = useApiError();
  const { showPass, toggle: toggleShowPass } = usePasswordVisibility();
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordFormValues>();

  const password = watch("password");

  const onSubmit = async (data: ResetPasswordFormValues) => {
    clearError();
    try {
      const result = await accountApi.resetPassword({ account, email, token, password: data.password });
      if (result.status === AuthStatus.Success) {
        window.history.replaceState(null, "", "/Account/Login");
        setSuccess(true);
      } else {
        setApiError(result.errorMessage ?? "An error occurred. Please try again.");
      }
    } catch {
      setApiError("An unexpected error occurred. Please try again.");
    }
  };

  if (success) {
    return (
      <>
        <StatusIconCircle variant="success" icon={Check} />
        <ViewHeading title="Password reset" subtitle="Your password has been reset successfully." />
        <SubmitButton type="button" onClick={() => onViewChange("login")}>
          Back to login
        </SubmitButton>
      </>
    );
  }

  return (
    <>
      <FormOverlay loading={isSubmitting}>

      <ViewHeading title="Reset Password" subtitle="Enter and confirm your new password." />

      {apiError && <ErrorAlert message={apiError} />}

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <LoginFormField
          label="New password"
          type={showPass ? "text" : "password"}
          placeholder="New password"
          autoComplete="new-password"
          error={errors.password?.message}
          disabled={isSubmitting}
          icon={<LockIcon size={16} color="#9CA3AF" strokeWidth={2} />}
          trailingElement={<PasswordVisibilityToggle showPass={showPass} onToggle={toggleShowPass} />}
          maxLength={PASSWORD_MAX_INPUT_LENGTH}
          {...register("password", {
            required: "Password is required",
            validate: validateNewPassword,
          })}
        />

        <LoginFormField
          label="Confirm password"
          type={showPass ? "text" : "password"}
          placeholder="Confirm new password"
          autoComplete="new-password"
          error={errors.confirmPassword?.message}
          disabled={isSubmitting}
          icon={<LockIcon size={16} color="#9CA3AF" strokeWidth={2} />}
          trailingElement={<PasswordVisibilityToggle showPass={showPass} onToggle={toggleShowPass} />}
          maxLength={PASSWORD_MAX_INPUT_LENGTH}
          {...register("confirmPassword", {
            required: "Please confirm your password",
            validate: {
              length: (value) => validatePasswordLength(value),
              matches: mustMatch(password),
            },
          })}
        />

        <div className="mb-3">
          <SubmitButton loading={isSubmitting}>Submit</SubmitButton>
        </div>
      </form>

      <BackToLoginLink onClick={() => onViewChange("login")} />
      </FormOverlay>
    </>
  );
};
